/**
 * `ENTITY_CHANGED`(`entity:changed`) 소비 규약 — 순수 모듈(React·store 무관).
 *
 * 서버가 현장·현장 하위 리소스·협력업체·그룹·멤버·현장 결재선의 생성/수정/삭제를 조직 room에
 * 브로드캐스트하면(`emitToOrganization`), web은 두 가지만 한다:
 *  1. `ENTITY_QUERY_KEYS` 표대로 react-query 캐시를 무효화한다(접두 매칭 — `['site']`는
 *     `['site', siteId]` 전부를 잡는다). 무효화는 멱등이라 행위자 본인에게 도달해도 무해하다.
 *  2. 삭제된 엔티티의 **상세 화면을 보고 있던 남**을 목록으로 보낸다(`resolveEntityDeletedNavigation`).
 *     행위자는 자기 흐름이 이미 이동시키므로(현장 삭제는 목록 페이지에서 일어난다) 제외한다.
 *
 * 이동 판정 주체는 **이 함수 하나**다 — `WebSocketProvider`만 부른다. 삭제 후 이동을 정하는 곳이
 * 여럿이면 매번 지는 쪽을 고친 것일 수 있다(`memory/feedback-count-who-moves-the-screen-not-what-you-fixed`).
 *
 * 키 표는 `git grep -ohE "queryKey: \[[^]]{0,60}\]" -- apps/web/src | sort | uniq -c`로 센 census(2026-09-19)
 * 기준이다. 새 react-query 키를 만들면 여기 행을 더한다 — 표에 없는 entity가 오면 무효화 없이
 * `console.warn` 1회(엔티티당)로 끝난다.
 *
 * 현장 `updated`는 이 이벤트로 오지 않는다 — 기존 `SITE_UPDATED`가 맡는다(그 핸들러가 `sites`·`site`를
 * 무효화한다). `ENTITY_CHANGED(site)`는 created·deleted만.
 */
import type { EntityChangedPayload } from '../types/websocket';
import { withSubjectParticle } from '@safetyhub/shared';

export type EntityChangedEntity = EntityChangedPayload['entity'];

/**
 * entity → 무효화할 query key **접두** 목록.
 *
 * - `site`: 목록·상세·담당자(상세 헤더가 담당자 목록을 함께 읽는다).
 * - `site-*`: 현장 상세 각 섹션의 키. `site-contact`는 세 이름을 쓴다 — 현장 상세 섹션(`site-contacts`),
 *   점검·보고서 폼의 담당자 선택(`siteContacts`), 점검별 담당자(`inspectionSiteContacts`).
 * - `company`·`group`: 협력업체·그룹 화면 자체는 `useEffect` 직접 조회라 react-query 키가 없다.
 *   대신 현장이 `company`를 embed하고 현장 목록이 `groupId` 스코프라 `sites`·`site`를 무효화한다.
 * - `member`: 멤버 목록·현장 담당자 목록.
 * - `site-approval-line-meta`: 소비처(`SiteApprovalLinesSection`·`ApprovalLineSectionImpl`)가 둘 다
 *   react-query를 쓰지 않는다(`useQuery` 0) — 무효화할 키가 **없다**. 그 화면이 react-query로 옮겨 오면
 *   여기 키를 더한다. 빈 배열은 「모른다」가 아니라 「확인했더니 없다」다.
 * - `announcement`: 소비 화면이 **셋이고 셋 다 배선됐다** — 고정 띠(`components/AnnouncementBand`) ·
 *   저작 목록(`pages/settings/Announcements`) · 구성원 게시판(`pages/announcements`). census
 *   (2026-10-27): 그 셋에서 `useQuery` **0건** ⇒ 무효화할 키가 **없다**. 위와 같은 뜻의 빈 배열이고
 *   전달은 `useEntityChanged` 버스가 맡는다. 빈 배열은 「모른다」가 아니라 **「확인했더니 없다」**다 —
 *   그 화면 중 하나가 react-query로 옮겨 오면 여기 키를 더하고 그 화면의 `useEntityChanged` 호출을
 *   걷는다(이벤트 하나에 무효화 하나).
 *   ⚠️ **그 셋을 세는 needle(api 모듈 경로)을 이 주석에 리터럴로 쓰지 않는다** — 쓰면 이 파일이
 *   그 census에 **자기 주석 때문에** 걸려 「3개」가 조용히 거짓이 된다(실측: 쓴 상태로 세니 4였다).
 */
export const ENTITY_QUERY_KEYS: Record<EntityChangedEntity, readonly (readonly string[])[]> = {
  site: [['sites'], ['site'], ['siteAssignments']],
  'site-location': [['site-locations']],
  'site-detail-location': [['site-detail-locations']],
  'site-process-type': [['site-process-types']],
  'site-equipment': [['site-equipment']],
  'site-contact': [['site-contacts'], ['siteContacts'], ['inspectionSiteContacts']],
  company: [['sites'], ['site']],
  group: [['sites'], ['site']],
  member: [['organizationMembers'], ['siteAssignments']],
  'site-approval-line-meta': [],
  announcement: [],
};

const warnedUnknownEntities = new Set<string>();

/**
 * payload의 entity에 해당하는 무효화 키 접두 목록. 표에 없는 entity면 빈 배열 + `console.warn` 1회.
 * (테스트에서 `warnedUnknownEntities`를 비우려면 `resetEntityChangedWarnings()`.)
 */
export function resolveEntityQueryKeys(entity: string): readonly (readonly string[])[] {
  const keys = (ENTITY_QUERY_KEYS as Record<string, readonly (readonly string[])[] | undefined>)[entity];
  if (keys) return keys;
  if (!warnedUnknownEntities.has(entity)) {
    warnedUnknownEntities.add(entity);
    console.warn(`[entityChanged] 무효화 표에 없는 entity: ${entity} — lib/entityChanged.ts에 행을 더할 것`);
  }
  return [];
}

export function resetEntityChangedWarnings(): void {
  warnedUnknownEntities.clear();
}

export interface EntityDeletedNavigation {
  to: string;
  toast: string;
}

/**
 * 상세 라우트가 있는 entity → 「그 상세를 보고 있는가」 판정 + 목록 경로.
 *
 * `App.tsx` 라우트 기준(2026-10-27 재확인): 상세 라우트를 가진 것은 현장뿐(`/sites/:siteId`,
 * `/sites/:siteId/edit`). 협력업체·그룹은 `/settings/organization`·`/settings/groups`, 멤버는 `/members`,
 * 공지는 `/announcements`·`/settings/announcements`(`announcements`로 grep해 **라우트 둘뿐**이고 `:id`
 * 세그먼트가 없다) — 전부 목록 화면이라 삭제돼도 이동할 곳이 없다(무효화만으로 목록에서 사라진다).
 * 현장 하위 리소스는 현장 상세의 섹션이라 역시 무효화만.
 */
const DETAIL_ROUTES: Partial<Record<EntityChangedEntity, { isDetailOf: (pathname: string, id: string) => boolean; list: string; label: string }>> = {
  site: {
    // `/sites/:id` · `/sites/:id/edit` · `/sites/:id/...` — `/sites/:id-other`는 잡지 않는다.
    isDetailOf: (pathname, id) => pathname === `/sites/${id}` || pathname.startsWith(`/sites/${id}/`),
    list: '/sites',
    label: '현장',
  },
};

/**
 * 삭제 이벤트를 받은 시점에 이동해야 하는가.
 *
 * 이동 조건 셋이 모두 참일 때만 `{ to, toast }`, 아니면 `null`:
 *  - `action === 'deleted'`
 *  - 행위자가 내가 아니다(`payload.actorUserId !== myUserId`; `actorUserId`가 null이면 시스템 행위자로 보아 이동)
 *  - 지금 보고 있는 경로가 그 엔티티의 상세다
 */
export function resolveEntityDeletedNavigation(
  pathname: string,
  payload: Pick<EntityChangedPayload, 'entity' | 'action' | 'id' | 'actorUserId' | 'name'>,
  myUserId: string | null | undefined,
): EntityDeletedNavigation | null {
  if (payload.action !== 'deleted') return null;
  if (payload.actorUserId && payload.actorUserId === myUserId) return null;
  const route = DETAIL_ROUTES[payload.entity];
  if (!route) return null;
  if (!route.isDetailOf(pathname, payload.id)) return null;
  const subject = payload.name ? `${route.label} «${payload.name}»` : route.label;
  return {
    to: route.list,
    toast: `${withSubjectParticle(subject)} 삭제되어 목록으로 이동했습니다`,
  };
}
